import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { COLORS, FONTS } from '../theme';
import { SCHOOL, IMAGES } from '../data/siteData';
import { Icon, ImageBox, Wave, float, floatSlow, wiggle, spin, drift, pulse, morph, popIn, marquee, useScrollY } from './common';

function Cloud({ top, size = 1, duration = 60, delay = 0, opacity = 0.9 }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        top,
        left: 0,
        width: 110 * size,
        height: 38 * size,
        borderRadius: 50,
        bgcolor: '#fff',
        opacity,
        animation: `${drift} ${duration}s linear ${delay}s infinite`,
        '&::before': { content: '""', position: 'absolute', top: -20 * size, left: 18 * size, width: 50 * size, height: 50 * size, borderRadius: '50%', bgcolor: '#fff' },
        '&::after': { content: '""', position: 'absolute', top: -30 * size, left: 52 * size, width: 40 * size, height: 40 * size, borderRadius: '50%', bgcolor: '#fff' },
      }}
    />
  );
}

function Sun() {
  return (
    <Box sx={{ position: 'absolute', top: { xs: 90, md: 130 }, right: { xs: -30, md: 50 }, width: { xs: 110, md: 150 }, aspectRatio: '1', zIndex: 1 }}>
      <Box sx={{ position: 'absolute', inset: -16, borderRadius: '50%', border: `7px dotted ${COLORS.yellow}`, animation: `${spin} 22s linear infinite` }} />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '50%',
          background: `radial-gradient(circle at 35% 35%, #FFE680, ${COLORS.yellow})`,
          boxShadow: '0 0 40px rgba(255,200,61,.8)',
          animation: `${pulse} 4s ease-in-out infinite`,
        }}
      >
        <Box sx={{ position: 'absolute', top: '36%', left: '26%', width: '9%', aspectRatio: '1', borderRadius: '50%', bgcolor: COLORS.navy }} />
        <Box sx={{ position: 'absolute', top: '36%', right: '26%', width: '9%', aspectRatio: '1', borderRadius: '50%', bgcolor: COLORS.navy }} />
        <Box sx={{ position: 'absolute', top: '52%', left: '30%', width: '40%', height: '20%', borderBottom: `5px solid ${COLORS.navy}`, borderRadius: '0 0 60px 60px' }} />
        <Box sx={{ position: 'absolute', top: '52%', left: '16%', width: '12%', aspectRatio: '1', borderRadius: '50%', bgcolor: 'rgba(255,77,151,.45)' }} />
        <Box sx={{ position: 'absolute', top: '52%', right: '16%', width: '12%', aspectRatio: '1', borderRadius: '50%', bgcolor: 'rgba(255,77,151,.45)' }} />
      </Box>
    </Box>
  );
}

const BLOCKS = [
  { ch: 'A', color: COLORS.pink, top: '24%', left: '3%', size: 62, delay: 0 },
  { ch: 'B', color: COLORS.blue, top: '62%', left: '1%', size: 54, delay: 1 },
  { ch: 'C', color: COLORS.green, top: '78%', left: '44%', size: 58, delay: 2 },
  { ch: '1', color: COLORS.orange, top: '14%', left: '47%', size: 50, delay: 1.5 },
  { ch: '2', color: COLORS.purple, top: '70%', right: '3%', size: 56, delay: 0.5 },
];

function FloatingBlocks({ y }) {
  return (
    <>
      {BLOCKS.map((b) => (
        <Box
          key={b.ch}
          sx={{
            position: 'absolute',
            top: b.top,
            left: b.left,
            right: b.right,
            display: { xs: 'none', md: 'flex' },
            width: b.size,
            height: b.size,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '16px',
            bgcolor: b.color,
            color: '#fff',
            fontFamily: FONTS.heading,
            fontWeight: 800,
            fontSize: b.size * 0.6,
            boxShadow: `0 8px 0 rgba(0,0,0,.14)`,
            transform: `translateY(${y * -0.08}px)`,
            zIndex: 1,
          }}
        >
          <Box sx={{ animation: `${wiggle} 4s ease-in-out ${b.delay}s infinite`, lineHeight: 1 }}>{b.ch}</Box>
        </Box>
      ))}
    </>
  );
}

function InfoPill({ icon, label, value, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: '#fff', borderRadius: 50, pl: 1, pr: 3, py: 1, boxShadow: '0 8px 20px rgba(27,42,107,.12)' }}>
      <Box sx={{ width: 44, height: 44, borderRadius: '50%', bgcolor: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={icon} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: COLORS.text, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</Typography>
        <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 800, color: COLORS.navy, fontSize: 18, lineHeight: 1.1 }}>{value}</Typography>
      </Box>
    </Box>
  );
}

function FloatBadge({ icon, text, color, sx }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        bgcolor: '#fff',
        borderRadius: 50,
        pl: 1,
        pr: 2.2,
        py: 0.8,
        boxShadow: '0 10px 24px rgba(27,42,107,.2)',
        animation: `${float} 5s ease-in-out infinite`,
        zIndex: 3,
        ...sx,
      }}
    >
      <Box sx={{ width: 38, height: 38, borderRadius: '50%', bgcolor: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={icon} sx={{ fontSize: 22 }} />
      </Box>
      <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 800, fontSize: 15, color: COLORS.navy, lineHeight: 1.1 }}>{text}</Typography>
    </Box>
  );
}

export function Marquee() {
  const words = ['Play', 'Learn', 'Make Friends', 'Build Confidence', 'Grow Together'];
  const colors = [COLORS.pink, COLORS.blue, COLORS.green, COLORS.orange, COLORS.purple];
  const row = words.map((w, i) => (
    <Box key={w} sx={{ display: 'flex', alignItems: 'center', gap: 3, px: 3, flexShrink: 0 }}>
      <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 800, fontSize: { xs: 24, md: 32 }, color: '#fff', whiteSpace: 'nowrap' }}>{w}</Typography>
      <Icon name="star" sx={{ color: colors[i % colors.length] === COLORS.yellow ? COLORS.navy : COLORS.yellow, fontSize: 28 }} />
    </Box>
  ));
  return (
    <Box sx={{ bgcolor: COLORS.navy, py: 1.5, overflow: 'hidden', transform: 'rotate(-1.2deg)', mx: -1, my: { xs: 1, md: 2 } }}>
      <Box sx={{ display: 'flex', width: 'max-content', animation: `${marquee} 28s linear infinite` }}>
        <Box sx={{ display: 'flex' }}>{row}</Box>
        <Box sx={{ display: 'flex' }}>{row}</Box>
        <Box sx={{ display: 'flex' }}>{row}</Box>
        <Box sx={{ display: 'flex' }}>{row}</Box>
      </Box>
    </Box>
  );
}

export default function Hero() {
  const y = useScrollY();

  return (
    <Box
      id="home"
      component="section"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 15, md: 24 },
        pb: { xs: 4, md: 6 },
        background: `linear-gradient(180deg, #BFE8FF 0%, ${COLORS.sky} 45%, #FFF3D6 100%)`,
      }}
    >
      <Cloud top="14%" size={1.2} duration={70} />
      <Cloud top="30%" size={0.8} duration={90} delay={-30} opacity={0.75} />
      <Cloud top="8%" size={0.9} duration={80} delay={-55} opacity={0.8} />
      <Sun />
      <FloatingBlocks y={y} />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' }, gap: { xs: 6, md: 4 }, alignItems: 'center' }}>
          <Box>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2.5,
                py: 0.8,
                mb: 2,
                borderRadius: 50,
                bgcolor: COLORS.pink,
                color: '#fff',
                fontFamily: FONTS.heading,
                fontWeight: 800,
                fontSize: { xs: 15, md: 17 },
                animation: `${popIn} .8s ease both`,
                boxShadow: '0 6px 0 #C2185B',
              }}
            >
              <Icon name="star" sx={{ fontSize: 20, color: COLORS.yellow }} /> Admission Open
            </Box>

            <Typography
              variant="h1"
              sx={{ fontSize: { xs: 46, sm: 60, md: 70 }, lineHeight: 1.02, color: COLORS.navy, textShadow: '0 4px 0 rgba(255,255,255,.9)' }}
            >
              <Box component="span" sx={{ color: COLORS.pink }}>Play.</Box>{' '}
              <Box component="span" sx={{ color: COLORS.blue }}>Learn.</Box>
              <br />
              <Box component="span" sx={{ color: COLORS.green }}>Grow</Box>{' '}
              <Box component="span" sx={{ color: COLORS.orange }}>Together</Box>
            </Typography>

            <Typography sx={{ mt: 2, fontFamily: FONTS.heading, fontWeight: 700, fontSize: { xs: 22, md: 28 }, color: COLORS.purple }}>
              {SCHOOL.tagline}
            </Typography>
            <Typography sx={{ mt: 2, fontSize: { xs: 16, md: 18 }, lineHeight: 1.75, maxWidth: 540 }}>
              Welcome to the Day Care at {SCHOOL.fullName}, Bangalore. A happy, safe and colourful place where little ones play, learn,
              make friends and build confidence every single day.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4 }}>
              <Button
                href={`https://wa.me/${SCHOOL.phoneRaw}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                endIcon={<Icon name="arrow" />}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: 18,
                  bgcolor: COLORS.green,
                  boxShadow: '0 7px 0 #2B8A3E',
                  '&:hover': { bgcolor: '#2fa845', transform: 'translateY(3px)', boxShadow: '0 4px 0 #2B8A3E' },
                  transition: 'all .2s ease',
                }}
              >
                Admission Enquiry
              </Button>
              <Button
                href="#programs"
                variant="contained"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: 18,
                  bgcolor: COLORS.yellow,
                  color: COLORS.navy,
                  boxShadow: '0 7px 0 #E0A800',
                  '&:hover': { bgcolor: '#ffd35e', transform: 'translateY(3px)', boxShadow: '0 4px 0 #E0A800' },
                  transition: 'all .2s ease',
                }}
              >
                Explore Programs
              </Button>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4 }}>
              <InfoPill icon="group" label="For Children" value={SCHOOL.ages} color={COLORS.pink} />
              <InfoPill icon="clock" label="Timings" value={SCHOOL.timings} color={COLORS.blue} />
            </Box>
          </Box>

          <Box sx={{ position: 'relative', transform: `translateY(${y * 0.05}px)` }}>
            <Box sx={{ position: 'relative', width: { xs: '92%', md: '100%' }, mx: 'auto', animation: `${morph} 9s ease-in-out infinite`, overflow: 'hidden', border: `10px solid #fff`, boxShadow: '0 24px 50px rgba(27,42,107,.25)' }}>
              <ImageBox src={IMAGES.hero} label="Hero image: happy children playing" color={COLORS.pink} ratio="1 / 1" radius={0} />
            </Box>
            <Box sx={{ position: 'absolute', bottom: { xs: -20, md: -10 }, left: { xs: -6, md: -34 }, width: { xs: 110, md: 150 }, borderRadius: '50%', overflow: 'hidden', border: '7px solid #fff', boxShadow: '0 14px 30px rgba(27,42,107,.25)', animation: `${floatSlow} 7s ease-in-out infinite`, zIndex: 2 }}>
              <ImageBox src={IMAGES.heroSmall1} label="Photo" color={COLORS.blue} ratio="1 / 1" radius={0} sx={{ p: 0 }} />
            </Box>
            <Box sx={{ position: 'absolute', top: { xs: -10, md: 20 }, right: { xs: -4, md: -30 }, width: { xs: 90, md: 120 }, borderRadius: '50%', overflow: 'hidden', border: '7px solid #fff', boxShadow: '0 14px 30px rgba(27,42,107,.25)', animation: `${floatSlow} 6s ease-in-out 1s infinite`, zIndex: 2 }}>
              <ImageBox src={IMAGES.heroSmall2} label="Photo" color={COLORS.green} ratio="1 / 1" radius={0} />
            </Box>
            <FloatBadge icon="shield" text="Safe and Secure Campus" color={COLORS.blue} sx={{ top: '8%', left: { xs: -4, md: -40 } }} />
            <FloatBadge icon="meal" text="Healthy Meals" color={COLORS.orange} sx={{ bottom: '10%', right: { xs: -4, md: -24 }, animationDelay: '1.5s' }} />
          </Box>
        </Box>
      </Container>

      <Box sx={{ mt: { xs: 6, md: 8 } }}>
        <Marquee />
      </Box>
      <Wave color="#ffffff" sx={{ mt: -0.5, mb: '-2px' }} />
    </Box>
  );
}
